import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  query, 
  where, 
  orderBy, 
  limit as firestoreLimit,
  onSnapshot,
  addDoc,
  serverTimestamp
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions, auth } from '../../lib/firebase';
import { ClubSchema, validateWithDetails, sanitizeString } from './validation/schemas';
import { logger } from '../../utils/logger';

export interface Club {
  id: string;
  name: string;
  slug: string;
  description: string;
  location: string;
  website: string;
  contactEmail: string;
  logoURL: string;
  bannerURL: string;
  status: 'active' | 'inactive';
  membershipType: 'open' | 'approval' | 'invite';
  isPublic: boolean;
  clubTypes: string[];
  createdAt: Date;
  updatedAt: Date;
  stats: {
    memberCount: number;
    routeCount: number;
    totalDistance: number;
    publicRouteCount?: number;
    memberRouteCount?: number;
    premiumRouteCount?: number;
  };
  routeAccess?: {
    defaultVisibility: 'public' | 'members-only' | 'premium-members';
    premiumPasswordHash?: string;
    premiumPasswordHint?: string;
    tiers?: {
      basic?: {
        name: string;
        description: string;
        routeCount: number;
      };
      premium?: {
        name: string;
        description: string;
        routeCount: number;
        price?: string;
      };
    };
  };
}

export interface Membership {
  id: string;
  userId: string;
  clubId: string;
  status: 'pending' | 'approved' | 'rejected';
  role: 'member' | 'admin';
  message: string;
  requestedAt: Date;
  joinedAt: Date | null;
}

// Cloud Functions
const createUserClubFunction = httpsCallable(functions, 'createUserClub');
const requestClubMembershipFunction = httpsCallable(functions, 'requestClubMembership');
const updateMembershipStatusFunction = httpsCallable(functions, 'updateMembershipStatus');
const getClubDetailsFunction = httpsCallable(functions, 'getClubDetails');
const getClubMembersFunction = httpsCallable(functions, 'getClubMembers');

// Club management functions - using Australia region Cloud Functions
export const createClub = async (clubData: Omit<Club, 'id' | 'createdAt' | 'updatedAt' | 'stats'>) => {
  try {
    console.log('Calling Australia region Cloud Function:', clubData)
    
    // Check authentication state
    const currentUser = auth.currentUser
    if (!currentUser) {
      throw new Error('User not authenticated with Firebase')
    }
    
    console.log('Calling createUserClub function in australia-southeast1')
    const result = await createUserClubFunction(clubData)
    console.log('Australia function result:', result)
    return result.data
  } catch (error) {
    console.error('Australia Cloud Function error:', error)
    
    // Fallback to direct Firestore if Cloud Function fails
    console.log('Falling back to direct Firestore approach...')
    
    // Generate unique slug
    let finalSlug = clubData.slug || clubData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
    let slugExists = true
    let counter = 1

    while (slugExists) {
      const existingClub = await getDoc(doc(db, 'clubs', finalSlug))
      if (!existingClub.exists()) {
        slugExists = false
      } else {
        finalSlug = `${clubData.slug || clubData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}-${counter}`
        counter++
      }
    }

    const clubId = finalSlug
    const currentUser = auth.currentUser!

    // Sanitize input data
    const sanitizedClubData = {
      ...clubData,
      name: sanitizeString(clubData.name, 200),
      slug: finalSlug,
      description: sanitizeString(clubData.description || '', 2000),
      location: sanitizeString(clubData.location, 200),
      website: clubData.website ? sanitizeString(clubData.website, 500) : undefined,
      contactEmail: clubData.contactEmail ? sanitizeString(clubData.contactEmail, 200) : undefined,
      logoURL: clubData.logoURL ? sanitizeString(clubData.logoURL, 500) : undefined,
      bannerURL: clubData.bannerURL ? sanitizeString(clubData.bannerURL, 500) : undefined,
      membershipType: clubData.membershipType || 'open',
      isPublic: clubData.isPublic !== undefined ? clubData.isPublic : true,
      clubTypes: clubData.clubTypes || [],
      ownerId: currentUser.uid,
      ownerDisplayName: currentUser.displayName || currentUser.email || 'Unknown',
      createdBy: currentUser.uid,
      createdAt: new Date(),
      updatedAt: new Date(),
      stats: { memberCount: 1, routeCount: 0, totalDistance: 0 }
    }
    
    // Validate club data
    const validatedClub = validateWithDetails(ClubSchema, sanitizedClubData, 'club')
    
    // Direct Firestore fallback
    const newClub = validatedClub
    
    await setDoc(doc(db, 'clubs', clubId), newClub)
    await setDoc(doc(db, 'memberships', `${currentUser.uid}_${clubId}`), {
      userId: currentUser.uid,
      clubId: clubId,
      status: 'approved',
      role: 'admin',
      joinedAt: new Date(),
      requestedAt: new Date()
    })
    
    console.log('Fallback: Club created with direct Firestore')
    return { success: true, id: clubId, slug: finalSlug }
  }
};

export const getClub = async (clubId: string): Promise<Club | null> => {
  try {
    const clubDoc = await getDoc(doc(db, 'clubs', clubId));
    if (clubDoc.exists()) {
      return {
        id: clubDoc.id,
        ...clubDoc.data()
      } as Club;
    }
    return null;
  } catch (error) {
    console.error('Error getting club from Firestore:', error);
    return null;
  }
};

export const getClubBySlug = async (slug: string): Promise<Club | null> => {
  try {
    const clubsRef = collection(db, 'clubs');
    const q = query(clubsRef, where('slug', '==', slug), firestoreLimit(1));
    const snapshot = await getDocs(q);
    
    if (!snapshot.empty) {
      const clubDoc = snapshot.docs[0];
      return {
        id: clubDoc.id,
        ...clubDoc.data()
      } as Club;
    }
    return null;
  } catch (error) {
    console.error('Error getting club by slug:', error);
    return null;
  }
};

export const getAllClubs = async (): Promise<Club[]> => {
  try {
    const clubsRef = collection(db, 'clubs');
    // Simplified query to avoid composite index requirement
    const q = query(clubsRef, where('status', '==', 'active'));
    const snapshot = await getDocs(q);
    
    // Sort by name in memory instead of in the query
    const clubs = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Club[];
    
    return clubs.sort((a, b) => a.name.localeCompare(b.name));
  } catch (error) {
    console.error('Error getting clubs:', error);
    return [];
  }
};

// Membership functions
export const requestMembership = async (clubId: string, message: string = '') => {
  const result = await requestClubMembershipFunction({ clubId, message });
  return result.data;
};

export const updateMembershipStatus = async (membershipId: string, status: 'approved' | 'rejected', role?: string) => {
  const result = await updateMembershipStatusFunction({ membershipId, status, role });
  return result.data;
};

export const getUserMemberships = async (userId: string): Promise<Membership[]> => {
  try {
    if (!userId) {
      console.error('getUserMemberships called with undefined userId');
      return [];
    }
    
    logger.debug('Getting memberships for user');
    const membershipsRef = collection(db, 'memberships');
    const q = query(
      membershipsRef, 
      where('userId', '==', userId),
      orderBy('requestedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    
    logger.debug('Found', snapshot.size, 'memberships');
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Membership[];
  } catch (error) {
    console.error('Error getting user memberships:', error);
    return [];
  }
};

export const getClubMembers = async (clubId: string): Promise<Membership[]> => {
  try {
    console.log('Calling getClubMembersFunction for clubId:', clubId);
    const result = await getClubMembersFunction({ clubId });
    console.log('Cloud Function result:', result.data);
    
    // Extract members array from the nested structure
    const members = result.data?.members || result.data;
    console.log('Extracted members:', members);
    
    return Array.isArray(members) ? members : [];
  } catch (error) {
    console.error('Cloud Function error, falling back to direct Firestore:', error);
    
    // Fallback to direct Firestore
    try {
      console.log('Querying Firestore for memberships with clubId:', clubId);
      const membershipsRef = collection(db, 'memberships');
      
      // Try without orderBy first in case joinedAt field is missing
      const q = query(
        membershipsRef,
        where('clubId', '==', clubId)
      );
      const snapshot = await getDocs(q);
      
      console.log('Firestore query returned', snapshot.size, 'documents');
      
      const memberships = snapshot.docs.map(doc => {
        const data = doc.data();
        console.log('Membership document:', doc.id, data);
        return {
          id: doc.id,
          ...data
        };
      }) as Membership[];
      
      console.log('Final memberships array:', memberships);
      return memberships;
    } catch (firestoreError) {
      console.error('Error getting club members from Firestore:', firestoreError);
      return [];
    }
  }
};

export const getPendingMemberships = async (clubId: string): Promise<Membership[]> => {
  try {
    const membershipsRef = collection(db, 'memberships');
    const q = query(
      membershipsRef,
      where('clubId', '==', clubId),
      where('status', '==', 'pending'),
      orderBy('requestedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Membership[];
  } catch (error) {
    console.error('Error getting pending memberships:', error);
    return [];
  }
};


// Check if user is club member
export const isClubMember = async (userId: string, clubId: string): Promise<boolean> => {
  try {
    const membershipRef = doc(db, 'memberships', `${userId}_${clubId}`);
    const membershipDoc = await getDoc(membershipRef);
    return membershipDoc.exists() && membershipDoc.data()?.status === 'approved';
  } catch (error) {
    console.error('Error checking club membership:', error);
    return false;
  }
};

// Check if user is club admin
export const isClubAdmin = async (userId: string, clubId: string): Promise<boolean> => {
  try {
    const membershipRef = doc(db, 'memberships', `${userId}_${clubId}`);
    const membershipDoc = await getDoc(membershipRef);
    const membership = membershipDoc.data();
    return membershipDoc.exists() && 
           membership?.status === 'approved' && 
           membership?.role === 'admin';
  } catch (error) {
    console.error('Error checking club admin status:', error);
    return false;
  }
};

// Update member status (approve/reject membership requests)
export const updateMemberStatus = async (membershipId: string, newStatus: 'approved' | 'rejected'): Promise<void> => {
  try {
    const result = await updateMembershipStatusFunction({ membershipId, status: newStatus });
    return result.data;
  } catch (error) {
    console.error('Cloud Function error, falling back to direct Firestore:', error);
    
    // Fallback to direct Firestore
    try {
      const membershipRef = doc(db, 'memberships', membershipId);
      const updateData: any = {
        status: newStatus,
        updatedAt: new Date()
      };
      
      if (newStatus === 'approved') {
        updateData.joinedAt = new Date();
      }
      
      await updateDoc(membershipRef, updateData);
      
      // If approved, update club member count
      if (newStatus === 'approved') {
        const membershipDoc = await getDoc(membershipRef);
        if (membershipDoc.exists()) {
          const membership = membershipDoc.data();
          const clubRef = doc(db, 'clubs', membership.clubId);
          const clubDoc = await getDoc(clubRef);
          if (clubDoc.exists()) {
            const clubData = clubDoc.data();
            await updateDoc(clubRef, {
              'stats.memberCount': (clubData.stats?.memberCount || 0) + 1,
              updatedAt: new Date()
            });
          }
        }
      }
    } catch (firestoreError) {
      console.error('Error updating membership status:', firestoreError);
      throw firestoreError;
    }
  }
};

// Update club settings
export const updateClubSettings = async (clubId: string, updates: Partial<Club>): Promise<void> => {
  try {
    const clubRef = doc(db, 'clubs', clubId);
    await updateDoc(clubRef, {
      ...updates,
      updatedAt: new Date()
    });
  } catch (error) {
    console.error('Error updating club settings:', error);
    throw error;
  }
};

// Submit club deletion request
export const submitClubDeletionRequest = async (clubId: string, reason: string): Promise<void> => {
  try {
    console.log('Starting deletion request submission for clubId:', clubId);
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    logger.debug('Current user authenticated');

    const deletionRequest = {
      clubId,
      adminId: currentUser.uid,
      reason,
      status: 'pending',
      requestedAt: new Date(),
      updatedAt: new Date()
    };

    console.log('Deletion request object:', deletionRequest);

    // Store deletion request in Firestore
    const requestId = `${clubId}_${Date.now()}`;
    const requestRef = doc(collection(db, 'clubDeletionRequests'), requestId);
    
    console.log('Submitting to Firestore with ID:', requestId);
    await setDoc(requestRef, deletionRequest);

    console.log('Club deletion request successfully submitted:', deletionRequest);
  } catch (error) {
    console.error('Error submitting club deletion request:', error);
    throw error;
  }
};

// Get club deletion request status
export const getClubDeletionRequest = async (clubId: string) => {
  try {
    const requestsRef = collection(db, 'clubDeletionRequests');
    // Simplified query to avoid index requirement for now
    const q = query(
      requestsRef,
      where('clubId', '==', clubId),
      where('status', '==', 'pending')
    );
    const snapshot = await getDocs(q);
    
    if (!snapshot.empty) {
      // Sort by requestedAt manually since we can't use orderBy without index
      const docs = snapshot.docs.sort((a, b) => {
        const aTime = a.data().requestedAt?._seconds || 0;
        const bTime = b.data().requestedAt?._seconds || 0;
        return bTime - aTime;
      });
      
      return {
        id: docs[0].id,
        ...docs[0].data()
      };
    }
    return null;
  } catch (error) {
    console.error('Error getting club deletion request:', error);
    return null;
  }
};

// Get all club deletion requests (for admin dashboard)
export const getAllClubDeletionRequests = async () => {
  try {
    console.log('Fetching all club deletion requests...');
    const requestsRef = collection(db, 'clubDeletionRequests');
    const q = query(requestsRef, orderBy('requestedAt', 'desc'));
    const snapshot = await getDocs(q);
    
    console.log('Found', snapshot.size, 'deletion requests');
    const requests = snapshot.docs.map(doc => {
      const data = {
        id: doc.id,
        ...doc.data()
      };
      console.log('Deletion request:', data);
      return data;
    });
    
    return requests;
  } catch (error) {
    console.error('Error getting all club deletion requests:', error);
    return [];
  }
};

// Approve deletion request and delete the club
export const approveDeletionRequest = async (requestId: string, clubId: string): Promise<void> => {
  try {
    // Update the deletion request status
    const requestRef = doc(db, 'clubDeletionRequests', requestId);
    await updateDoc(requestRef, {
      status: 'approved',
      updatedAt: new Date()
    });

    // Delete the club document
    const clubRef = doc(db, 'clubs', clubId);
    await deleteDoc(clubRef);

    // Delete all memberships for this club
    const membershipsRef = collection(db, 'memberships');
    const membershipQuery = query(membershipsRef, where('clubId', '==', clubId));
    const membershipSnapshot = await getDocs(membershipQuery);
    
    const deletePromises = membershipSnapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deletePromises);

    console.log('Club deletion approved and executed:', clubId);
  } catch (error) {
    console.error('Error approving deletion request:', error);
    throw error;
  }
};

// Deny deletion request
export const denyDeletionRequest = async (requestId: string): Promise<void> => {
  try {
    const requestRef = doc(db, 'clubDeletionRequests', requestId);
    await updateDoc(requestRef, {
      status: 'denied',
      updatedAt: new Date()
    });

    console.log('Club deletion request denied:', requestId);
  } catch (error) {
    console.error('Error denying deletion request:', error);
    throw error;
  }
};

// Submit club creation request
export const submitClubCreationRequest = async (clubData: Omit<Club, 'id' | 'createdAt' | 'updatedAt' | 'stats'>): Promise<void> => {
  try {
    console.log('Starting club creation request submission:', clubData);
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    const creationRequest = {
      ...clubData,
      requestedBy: currentUser.uid,
      requestedByEmail: currentUser.email,
      status: 'pending',
      requestedAt: new Date(),
      updatedAt: new Date()
    };

    console.log('Club creation request object:', creationRequest);

    // Store creation request in Firestore
    const requestId = `${clubData.slug || clubData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}_${Date.now()}`;
    const requestRef = doc(collection(db, 'clubCreationRequests'), requestId);
    
    console.log('Submitting to Firestore with ID:', requestId);
    await setDoc(requestRef, creationRequest);

    console.log('Club creation request successfully submitted:', creationRequest);
  } catch (error) {
    console.error('Error submitting club creation request:', error);
    throw error;
  }
};

// Get all club creation requests (for admin dashboard)
export const getAllClubCreationRequests = async () => {
  try {
    console.log('Fetching all club creation requests...');
    const requestsRef = collection(db, 'clubCreationRequests');
    const snapshot = await getDocs(requestsRef);
    
    console.log('Found', snapshot.size, 'creation requests');
    const requests = snapshot.docs.map(doc => {
      const data = {
        id: doc.id,
        ...doc.data()
      };
      console.log('Creation request:', data);
      return data;
    }).sort((a, b) => {
      // Sort manually by requestedAt
      const aTime = a.requestedAt?._seconds || 0;
      const bTime = b.requestedAt?._seconds || 0;
      return bTime - aTime;
    });
    
    return requests;
  } catch (error) {
    console.error('Error getting all club creation requests:', error);
    return [];
  }
};

// Approve club creation request and create the club
export const approveClubCreationRequest = async (requestId: string): Promise<void> => {
  try {
    // Get the request data
    const requestRef = doc(db, 'clubCreationRequests', requestId);
    const requestDoc = await getDoc(requestRef);
    
    if (!requestDoc.exists()) {
      throw new Error('Creation request not found');
    }
    
    const requestData = requestDoc.data();
    
    // Generate unique slug
    let finalSlug = requestData.slug || requestData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    let slugExists = true;
    let counter = 1;

    while (slugExists) {
      const existingClub = await getDoc(doc(db, 'clubs', finalSlug));
      if (!existingClub.exists()) {
        slugExists = false;
      } else {
        finalSlug = `${requestData.slug || requestData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}-${counter}`;
        counter++;
      }
    }

    const clubId = finalSlug;

    // Create the club
    const newClub = {
      name: requestData.name,
      slug: finalSlug,
      description: requestData.description,
      location: requestData.location,
      website: requestData.website,
      contactEmail: requestData.contactEmail,
      logoURL: requestData.logoURL || '',
      bannerURL: requestData.bannerURL || '',
      status: 'active',
      membershipType: requestData.membershipType || 'open',
      isPublic: requestData.isPublic !== undefined ? requestData.isPublic : true,
      clubTypes: requestData.clubTypes || [],
      createdBy: requestData.requestedBy,
      createdAt: new Date(),
      updatedAt: new Date(),
      stats: { memberCount: 1, routeCount: 0, totalDistance: 0 }
    };
    
    await setDoc(doc(db, 'clubs', clubId), newClub);
    
    // Create admin membership for the requester
    await setDoc(doc(db, 'memberships', `${requestData.requestedBy}_${clubId}`), {
      userId: requestData.requestedBy,
      clubId: clubId,
      status: 'approved',
      role: 'admin',
      joinedAt: new Date(),
      requestedAt: new Date()
    });

    // Update the creation request status
    await updateDoc(requestRef, {
      status: 'approved',
      approvedAt: new Date(),
      clubId: clubId,
      updatedAt: new Date()
    });

    console.log('Club creation approved and executed:', clubId);
  } catch (error) {
    console.error('Error approving club creation request:', error);
    throw error;
  }
};

// Deny club creation request
export const denyClubCreationRequest = async (requestId: string, reason?: string): Promise<void> => {
  try {
    const requestRef = doc(db, 'clubCreationRequests', requestId);
    await updateDoc(requestRef, {
      status: 'denied',
      deniedReason: reason || 'No reason provided',
      deniedAt: new Date(),
      updatedAt: new Date()
    });

    console.log('Club creation request denied:', requestId);
  } catch (error) {
    console.error('Error denying club creation request:', error);
    throw error;
  }
};

// Get pending admin request counts
export const getAdminPendingRequestCounts = async (): Promise<{
  creationRequests: number;
  deletionRequests: number;
  total: number;
}> => {
  try {
    const [creationReqs, deletionReqs] = await Promise.all([
      getDocs(query(collection(db, 'clubCreationRequests'), where('status', '==', 'pending'))),
      getDocs(query(collection(db, 'clubDeletionRequests'), where('status', '==', 'pending')))
    ])

    const creationCount = creationReqs.size
    const deletionCount = deletionReqs.size

    return {
      creationRequests: creationCount,
      deletionRequests: deletionCount,
      total: creationCount + deletionCount
    }
  } catch (error) {
    console.error('Failed to get admin pending request counts:', error)
    return {
      creationRequests: 0,
      deletionRequests: 0,
      total: 0
    }
  }
}

// Subscribe to admin pending request changes
export const subscribeToAdminPendingRequests = (
  onUpdate: (counts: {
    creationRequests: number;
    deletionRequests: number;
    total: number;
  }) => void
): (() => void) => {
  const unsubscribes: (() => void)[] = []
  
  let creationCount = 0
  let deletionCount = 0
  
  const updateCounts = () => {
    const total = creationCount + deletionCount
    onUpdate({
      creationRequests: creationCount,
      deletionRequests: deletionCount,
      total
    })
  }
  
  // Subscribe to creation requests
  const creationQuery = query(
    collection(db, 'clubCreationRequests'),
    where('status', '==', 'pending')
  )
  const creationUnsubscribe = onSnapshot(creationQuery, (snapshot) => {
    creationCount = snapshot.size
    updateCounts()
  }, (error) => {
    console.error('Error listening to creation requests:', error)
    creationCount = 0
    updateCounts()
  })
  
  // Subscribe to deletion requests
  const deletionQuery = query(
    collection(db, 'clubDeletionRequests'),
    where('status', '==', 'pending')
  )
  const deletionUnsubscribe = onSnapshot(deletionQuery, (snapshot) => {
    deletionCount = snapshot.size
    updateCounts()
  }, (error) => {
    console.error('Error listening to deletion requests:', error)
    deletionCount = 0
    updateCounts()
  })
  
  unsubscribes.push(creationUnsubscribe, deletionUnsubscribe)
  
  // Return cleanup function
  return () => {
    unsubscribes.forEach(unsubscribe => unsubscribe())
  }
}